/**
 * `AlertProvider` cho web — Prompt.md §19, §20.
 *
 * Đây là mặt đối diện của ranh giới §18: `alert-core` đã quyết định xong "nói câu gì"; file này
 * chỉ biết cách phát ra tiếng trên trình duyệt. Lên React Native, đây là file duy nhất bị thay.
 */

import type { AlertProvider } from "@studystraight/alert-core";
import type { AlertMessageKey } from "@studystraight/shared-types";

import { playAlertClip } from "./alertClips.js";
import { getAudioContext, isAudioUnlocked, resumeAudioContext } from "./audioUnlock.js";

const VOICE_LANG = "vi-VN";

/** Chrome trả `getVoices()` rỗng ở lần gọi đầu và nạp danh sách sau — chờ, nhưng có hạn. */
const VOICE_WAIT_MS = 2000;

/** §20 — chậm hơn bình thường một chút cho trẻ nhỏ, nhưng không đến mức nghe như máy hỏng. */
const SPEECH_RATE = 0.95;

/**
 * Chờ utterance **bắt đầu** đọc. Hết hạn = coi như speech chết, hạ xuống chime + băng chữ.
 *
 * Có thật trên iOS: `speak()` nhận utterance, không throw, rồi không bao giờ bắn `start`/`end`/
 * `error` — hay gặp nhất sau `cancel()` hoặc sau khi tab vừa quay lại từ background. Không có mốc
 * chờ này thì `await` ở dưới treo vĩnh viễn: trẻ không nghe gì, UI không hiện gì, và log cũng không
 * có lỗi nào để đọc.
 *
 * 2.5s vì lần đọc đầu tiên phải nạp voice data (đo trên máy thật: ~0.3–1.2s).
 */
const SPEECH_START_TIMEOUT_MS = 2500;

/** Trần cho cả câu, tính từ lúc bắt đầu đọc: câu dài nhất của §20 dưới 6s ở rate 0.95. */
const SPEECH_END_TIMEOUT_MS = 15_000;

export interface WebAlertProviderOptions {
  /**
   * Gọi khi không có giọng tiếng Việt: UI phải hiện băng chữ lớn thay vì đọc.
   *
   * Không fallback sang giọng `en-US` (một số Android thiếu TTS data tiếng Việt): câu tiếng Việt
   * đọc bằng giọng Anh nghe không hiểu được, và với trẻ 6–12 tuổi thì một câu lạ phát ra giữa
   * lúc đang học còn tệ hơn im lặng.
   */
  onTextFallback?: (text: string) => void;
}

interface SpeechState {
  supported: boolean;
  voice: SpeechSynthesisVoice | null;
}

/** Danh sách giọng có thể chưa nạp xong ngay khi app mở. */
async function loadVoices(): Promise<SpeechSynthesisVoice[]> {
  const synth = window.speechSynthesis;
  const immediate = synth.getVoices();
  if (immediate.length > 0) return immediate;

  return new Promise((resolve) => {
    const done = (): void => {
      clearTimeout(timer);
      synth.removeEventListener("voiceschanged", done);
      resolve(synth.getVoices());
    };
    const timer = setTimeout(done, VOICE_WAIT_MS);
    synth.addEventListener("voiceschanged", done);
  });
}

/**
 * Chọn giọng vi-VN. `startsWith("vi")` chứ không so bằng: các thiết bị trả về "vi-VN", "vi_VN"
 * và cả "vi" — so bằng chuỗi chính xác sẽ bỏ mất giọng đang có sẵn trên máy.
 */
function pickVietnameseVoice(voices: readonly SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  return voices.find((v) => v.lang.toLowerCase().replace("_", "-").startsWith("vi")) ?? null;
}

export class WebAlertProvider implements AlertProvider {
  private speech: SpeechState = { supported: false, voice: null };
  private initialized = false;

  constructor(private readonly options: WebAlertProviderOptions = {}) {}

  /**
   * Nạp danh sách giọng. Nên gọi ở màn Setup (trước khi phiên bắt đầu) để lần cảnh báo đầu tiên
   * không phải chờ tới 2 giây — nhưng không bắt buộc: `speak()` tự initialize nếu chưa.
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
    if (!("speechSynthesis" in window)) return;
    const voices = await loadVoices();
    this.speech = { supported: true, voice: pickVietnameseVoice(voices) };
  }

  isAvailable(): boolean {
    return isAudioUnlocked() && (this.speech.voice !== null || getAudioContext() !== null);
  }

  async speak(text: string, key: AlertMessageKey): Promise<void> {
    /*
     * File thu sẵn trước, TTS sau — không phải ngược lại.
     *
     * TTS là đường đã đo thấy hỏng trên máy thật (Android: đứt giữa câu; iOS: nhận utterance rồi im).
     * File mp3 thì hoặc phát đủ, hoặc không phát; không có trạng thái nửa vời phụ thuộc engine giọng
     * của từng máy. Chi tiết trong alertClips.ts.
     */
    const clip = await playAlertClip(key);
    if (clip !== "unavailable") {
      /*
       * `fallback` (đã phát generic.mp3 vì thiếu file riêng) **không** gọi `onTextFallback`: cờ đó
       * hiện băng "máy không có giọng tiếng Việt", ở đây thì sai. Trẻ vẫn nghe được một câu nhắc
       * đúng, và câu cụ thể vẫn hiện trên UI qua `snapshot.lastMessage`.
       */
      return;
    }

    await this.initialize();

    if (!this.speech.supported || this.speech.voice === null) {
      // Không đọc được tiếng Việt → chime + băng chữ. Vẫn phải phát ra *một* tín hiệu, vì trẻ
      // đang nhìn vào vở chứ không nhìn màn hình.
      await this.chime();
      this.options.onTextFallback?.(text);
      return;
    }

    const synth = window.speechSynthesis;

    // Cắt câu đang đọc: cảnh báo mới luôn liên quan hơn cảnh báo 25 giây trước, và hai câu chồng
    // nhau thì trẻ không hiểu câu nào. Nhưng CHỈ khi thật sự đang có câu — `cancel()` trên một hàng
    // đợi rỗng là một trong những cách làm utterance kế tiếp chết câm trên iOS.
    if (synth.speaking || synth.pending) synth.cancel();
    // iOS pause synthesizer khi tab mất visible và không tự chạy lại. Không có dòng này thì cảnh
    // báo đầu tiên sau khi mở lại máy sẽ bị xếp hàng im lặng cho tới hết phiên.
    if (synth.paused) synth.resume();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = this.speech.voice;
    utterance.lang = this.speech.voice.lang || VOICE_LANG;
    utterance.rate = SPEECH_RATE;

    const spoke = await new Promise<boolean>((resolve) => {
      // Chỉ resolve một lần: trên vài bản Safari cả `end` và `error` đều bắn.
      let settled = false;
      let started = false;
      let timer: ReturnType<typeof setTimeout> | undefined;

      const settle = (value: boolean): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      };

      utterance.addEventListener("start", () => {
        // Đã ra tiếng → đổi từ mốc chờ ngắn (chưa đọc) sang trần cho cả câu.
        started = true;
        clearTimeout(timer);
        timer = setTimeout(() => settle(true), SPEECH_END_TIMEOUT_MS);
      });
      utterance.addEventListener("end", () => settle(true));
      // Lỗi sau khi đã ra tiếng: câu bị cắt giữa, nhưng trẻ đã nghe — chime thêm chỉ gây nhiễu.
      // Lỗi trước khi ra tiếng: coi như không nói được, hạ xuống chime + băng chữ.
      utterance.addEventListener("error", () => settle(started));

      timer = setTimeout(() => settle(false), SPEECH_START_TIMEOUT_MS);
      synth.speak(utterance);
    });

    if (!spoke) {
      // Speech nhận utterance rồi im: huỷ để nó không đọc muộn chồng lên cảnh báo sau, rồi phát tín
      // hiệu bằng đường còn sống. Trẻ đang nhìn vở, im lặng hoàn toàn là mất luôn cảnh báo.
      synth.cancel();
      await this.chime();
      this.options.onTextFallback?.(text);
    }
  }

  /**
   * Chime tổng hợp bằng WebAudio, không dùng file mp3: bớt một asset phải tải và không phụ thuộc
   * autoplay policy của `<audio>`. Hai note đi lên (~200ms) — báo hiệu, không phải báo động.
   */
  async chime(): Promise<void> {
    const context = getAudioContext();
    if (!context) return;

    /*
     * Context bị suspend giữa phiên là chuyện thường, không phải ngoại lệ: iOS suspend mỗi lần tab
     * mất visible (khoá màn hình, Siri, cuộc gọi) và không tự chạy lại. Trên context suspend thì
     * `currentTime` đứng, nên `osc.start(now + …)` hẹn vào một mốc đã qua khi context sống lại →
     * hoặc mất tiếng, hoặc phát dồn cả loạt cùng lúc.
     *
     * `await` được ở đây vì chime không cần user gesture (khoá đã mở từ màn Setup) — chỉ *lần đầu*
     * mới bắt buộc đồng bộ trong gesture.
     */
    if (!(await resumeAudioContext())) return;

    const now = context.currentTime;
    const gain = context.createGain();
    gain.connect(context.destination);
    // Envelope thay vì bật/tắt biên độ: cắt sóng vuông tạo tiếng "pop" nghe rất khó chịu.
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.18, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.24);

    for (const [index, frequency] of [660, 880].entries()) {
      const osc = context.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(frequency, now + index * 0.1);
      osc.connect(gain);
      osc.start(now + index * 0.1);
      osc.stop(now + index * 0.1 + 0.12);
    }

    await new Promise<void>((resolve) => {
      setTimeout(resolve, 260);
    });
  }

  /** iOS không có `navigator.vibrate` — no-op im lặng, đó là hành vi mong đợi (§19). */
  vibrate(pattern: readonly number[]): void {
    const vibrator = navigator as Navigator & { vibrate?: (p: number | number[]) => boolean };
    try {
      vibrator.vibrate?.([...pattern]);
    } catch {
      // Một số máy throw khi tab không được focus. Không phải lỗi cần báo.
    }
  }

  /** Có giọng tiếng Việt hay không — UI dùng để quyết định hiện băng chữ (§20). */
  get hasVietnameseVoice(): boolean {
    return this.speech.voice !== null;
  }
}
